import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function DetailGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("grid gap-3 sm:grid-cols-2 lg:grid-cols-4", className)}>{children}</div>;
}

export function DetailFact({
  label,
  value,
  className,
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0 rounded-md border p-3", className)}>
      <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 break-words text-sm">{emptyNode(value)}</div>
    </div>
  );
}

export function MetricStrip({
  children,
}: {
  children: ReactNode;
}) {
  return <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>;
}

export function Metric({
  label,
  value,
  note,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
      <div className="mt-2 text-2xl font-semibold tracking-normal">{emptyNode(value)}</div>
      {note && <div className="mt-1 text-xs text-muted-foreground">{note}</div>}
    </div>
  );
}

export function DetailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

export function MetadataTable({
  value,
  emptyLabel = "No metadata exposed.",
}: {
  value?: Record<string, unknown> | null;
  emptyLabel?: string;
}) {
  const entries = Object.entries(value ?? {});
  if (entries.length === 0) {
    return (
      <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <tbody>
          {entries.map(([key, entryValue]) => (
            <tr key={key} className="border-b last:border-b-0">
              <th className="w-44 bg-muted/30 px-3 py-2 text-left align-top font-medium text-muted-foreground">
                {key}
              </th>
              <td className="px-3 py-2 align-top">{formatUnknown(entryValue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function formatDateTime(value?: string | number | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function formatUnknown(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return "None";
    return value.map((item) => formatUnknown(item)).join(", ");
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return "None";
    return entries.map(([key, entryValue]) => `${key}: ${formatUnknown(entryValue)}`).join("; ");
  }
  return String(value);
}

export function truncateMiddle(value?: string | number | null, visible = 18): string {
  if (!value) return "-";
  const text = String(value);
  if (text.length <= visible * 2 + 3) return text;
  return `${text.slice(0, visible)}...${text.slice(-visible)}`;
}

function emptyNode(value: ReactNode) {
  return value === null || value === undefined || value === "" ? "-" : value;
}
